import eventEmitter from "./eventEmitter";
import {EventEmitterTypes} from "./eventEmitterEnum";


interface SplitAddress {
    streetNumber: string,
    streetPrefix: string,
    streetName: string,
    streetSuffix: string,
    streetPostDirection: string,
    city: string,
    county: string,
    state: string,
    postalCode: string,
}


export function IgnoreError(error: any) {console.log(error)}

export function onChangeTextInputShouldOnlyBeNumber(e) {
    e.target.value = e.target.value.replace(/\D/g, "");
}

export function getAverageCenterOfPolygon(path: { lat: number; lng: number }[]) {
    const { lat, lng } = path.reduce(
        (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
        { lat: 0, lng: 0 }
    );
    return { lat: lat / path.length, lng: lng / path.length };
}

export function capitalize(str: string): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function SplitAddressFromGoogleAddressComponents(components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
}>): SplitAddress {
    const DIRECTIONS = new Set(['N','S','E','W','NE','NW','SE','SW', 'NORTH', 'SOUTH', 'WEST', 'EAST', 'NORTHEAST', 'NORTHWEST', 'SOUTHEAST', 'SOUTHWEST']);

    const lookup = (type: string) =>
        components.find(c => c.types.includes(type))?.short_name || '';

    const county = lookup("administrative_area_level_2")
    const streetNumber= lookup('street_number');
    const city= lookup('locality');
    const state= lookup('administrative_area_level_1');
    const postalCode= lookup('postal_code');

    const routeShort= lookup('route');         // 'Main St' or 'N Main St NW'
    const parts    = routeShort.split(' ').filter(p => !!p);

    let streetPrefix        = null;
    let streetName          = null;
    let streetSuffix        = null;
    let streetPostDirection = null;

    // first part is a dir
    if (parts.length > 0 && DIRECTIONS.has(capitalize(parts[0]))) {
        streetPrefix = parts.shift()!;
    }

    // last part is dir
    if (parts.length > 1 && DIRECTIONS.has(capitalize(parts[parts.length-1]))) {
        streetPostDirection = parts.pop()!;
    }

    // assume last part is the suffix
    if (parts.length >= 2) {
        streetSuffix = parts.pop()!;
        streetName   = parts.join(' ');
    } else if (parts.length === 1) {
        // no clear splits. all one
        streetName = parts[0];
    }

    return {
        streetNumber,
        streetPrefix,
        streetName,
        streetSuffix,
        streetPostDirection,
        city,
        county,
        state,
        postalCode
    };
}

export const isNullOrEmpty = (str) => {
    return str === null || str === undefined || str === "";
};

export function getBusinessOrCustomerName(customer){
    //filter boolean removes any falsy values (i think idk i saw it on stack overflow)
    return isNullOrEmpty(customer.businessName) ? [customer.firstName, customer.lastName].filter(Boolean).join(' ') : customer.businessName;
}

export function round5(x)
{
    return Math.ceil(x / 5) * 5;
}

export function splitAddressIntoStreetAndCityStateZip(fullAddress: string): [string, string] {
    const parts = fullAddress.split(',').map(part => part.trim());

    if (parts.length < 3) {
        return [fullAddress, ''];
    }

    const street = parts[0];
    const cityStateZip = `${parts[1]}, ${parts[2]}`;

    return [street, cityStateZip];
}

export function formatStandardizedAddress(c: any): string {
    //typically sends in customer data (c)
    if (c.standardizedAddress) return c.standardizedAddress;

    const streetParts = [
        c.streetNumber,
        c.streetPrefix,
        c.streetName,
        c.streetSuffix,
        c.streetPostDirection
    ]
        .filter(p => !!p)
        .join(" ");

    const cityStateZip = [
        c.city,
        c.state,
        c.postalCode
    ]
        .filter(p => !!p)
        .join(" ");

    return `${streetParts}, ${cityStateZip}`;
}

export function validateCardNumber(cardNumber: string): boolean {
    const cleaned = cardNumber.replace(/\D/g, '');
    let sum = 0;
    let shouldDouble = false;

    for (let i = cleaned.length - 1; i >= 0; i--) {
        let digit = parseInt(cleaned.charAt(i), 10);
        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
        shouldDouble = !shouldDouble;
    }

    return cleaned.length >= 13 && sum % 10 === 0;
}

export function formatCardNumber(value: string): string {
    const cleaned = value.replace(/\D/g, '')
    const cardType = getCardType(cleaned)

    if (cardType === 'American Express') {
        const part1 = cleaned.substring(0, 4)
        const part2 = cleaned.length > 4 ? cleaned.substring(4, 10) : ''
        const part3 = cleaned.length > 10 ? cleaned.substring(10, 15) : ''
        return [part1, part2, part3].filter(Boolean).join(' ')
    }

    const sliced = cleaned.slice(0, 19)
    const groups = sliced.match(/.{1,4}/g)
    return groups ? groups.join(' ') : ''
}

export function formatWebId(value: string): string {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
    const part1 = cleaned.slice(0, 4);
    const part2 = cleaned.slice(4, 8);
    const rest = cleaned.slice(8);

    if (part2 && rest) return `${part1}-${part2}-${rest}`;
    if (part2) return `${part1}-${part2}`;
    return part1;
}

export function toUTCISOString(localDate: Date): string {
    return new Date(Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate()
    )).toISOString();
}
export function requestIsSidebarOpen(): Promise<boolean> {
    return new Promise((resolve) => {
        eventEmitter.emit(EventEmitterTypes.GET_IS_SIDEBAR_OPEN, (isOpen: boolean) => {
            resolve(isOpen);
        });
    });
}

export function getCardType(cardNumber) {
    const num = cardNumber.replace(/\D/g, "");

    if (/^4/.test(num)) {
        return "Visa";
    }
    if (/^(5[1-5]|2(?:2[2-9]|[3-6][0-9]|7[01]|720))/.test(num)) {
        return "Mastercard";
    }
    if (/^3[47]/.test(num)) {
        return "American Express";
    }
    if (/^(6011|65|64[4-9])/.test(num)) {
        return "Discover";
    }
    return "unknown";
}

export interface LatLng {
    lat: number;
    lng: number;
}
export function GetGoogleMapsCoordsFromAddress(standardizedAddress: string): Promise<LatLng> {
    return new Promise((resolve, reject) => {
        // First, check that google.maps.Geocoder is available
        if (
            !window.google ||
            !window.google.maps ||
            typeof window.google.maps.Geocoder !== 'function'
        ) {
            reject(
                new Error(
                    'Geocoding library not loaded. Make sure you called useMapsLibrary("geocoding") or otherwise loaded Google Maps Geocoder.'
                )
            );
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
            { address: standardizedAddress },
            (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    resolve({ lat: loc.lat(), lng: loc.lng() });
                } else {
                    reject(new Error(`Geocoding failed: ${status}`));
                }
            }
        );
    });
}